import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { FeatureFlag, FlagEvaluationContext, Role } from "./types";
import { evaluateFlag } from "./evaluate";
import {
  getLocalOverride,
  getUrlOverrides,
  installConsoleBridge,
} from "./dev-tools";

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface FlagContextValue {
  readonly flags: ReadonlyMap<string, FeatureFlag>;
  readonly isLoading: boolean;
  readonly evaluate: (flagKey: string) => boolean;
}

const FlagContext = createContext<FlagContextValue | null>(null);

// ---------------------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------------------

async function fetchFlags(): Promise<FeatureFlag[]> {
  const res = await fetch(
    `${import.meta.env.VITE_API_URL ?? ""}/api/flags`,
    { credentials: "include" },
  );
  if (!res.ok) throw new Error(`Failed to fetch flags: ${res.status}`);
  const data = await res.json();
  return data.flags as FeatureFlag[];
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface FlagProviderProps {
  readonly userId: string;
  readonly role: Role;
  readonly children: ReactNode;
}

export function FlagProvider({ userId, role, children }: FlagProviderProps) {
  const queryClient = useQueryClient();

  const { data: flagList = [], isLoading } = useQuery({
    queryKey: ["feature-flags"],
    queryFn: fetchFlags,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });

  // Build lookup map
  const flagMap = useMemo(() => {
    const map = new Map<string, FeatureFlag>();
    for (const flag of flagList) {
      map.set(flag.key, flag);
    }
    return map;
  }, [flagList]);

  // Evaluation context
  const environment = import.meta.env.PROD
    ? "production"
    : import.meta.env.MODE === "staging"
      ? "staging"
      : "dev";

  const evalCtx = useMemo<FlagEvaluationContext>(
    () => ({ userId, role, environment: environment as FlagEvaluationContext["environment"] }),
    [userId, role, environment],
  );

  // URL overrides (parsed once)
  const urlOverrides = useMemo(() => getUrlOverrides(), []);

  // Subscribe to Supabase Realtime for live flag toggles
  useEffect(() => {
    const channel = supabase
      .channel("feature-flags-changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "feature_flags" },
        () => {
          queryClient.invalidateQueries({ queryKey: ["feature-flags"] });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  // Install console bridge in dev
  useEffect(() => {
    installConsoleBridge();
  }, []);

  // Evaluation function
  const evaluate = useMemo(() => {
    return (flagKey: string): boolean => {
      // Dev overrides take precedence (only in non-prod)
      if (!import.meta.env.PROD) {
        const urlOverride = urlOverrides.get(flagKey);
        if (urlOverride !== undefined) return urlOverride;

        const localOverride = getLocalOverride(flagKey);
        if (localOverride !== undefined) return localOverride;
      }

      const flag = flagMap.get(flagKey);
      if (!flag) return false;

      return evaluateFlag(flag, evalCtx, flagMap);
    };
  }, [flagMap, evalCtx, urlOverrides]);

  const value = useMemo<FlagContextValue>(
    () => ({ flags: flagMap, isLoading, evaluate }),
    [flagMap, isLoading, evaluate],
  );

  return (
    <FlagContext.Provider value={value}>{children}</FlagContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Raw context hook (internal use)
// ---------------------------------------------------------------------------

export function useFlagContext(): FlagContextValue {
  const ctx = useContext(FlagContext);
  if (!ctx) {
    throw new Error("useFlagContext must be used within a FlagProvider");
  }
  return ctx;
}
